import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Apple, Lock, Sparkles } from "lucide-react";

const VARIETIES = ["Avocado", "Citrus", "Mango", "Lychee", "Fig"];
const MATURITY = ["Seedling", "Mature Pup/Grafted", "Actively Fruiting"];
const HEALTH = ["Pristine", "Monitoring", "Action Required"];
const MOISTURE = ["Excessively Compacted/Flooded", "Well-Drained Organic Compost"];

export const FruitProfile = ({ treeId, initial, locked, onSaved }) => {
  const navigate = useNavigate();
  const known = VARIETIES.includes(initial?.variety);
  const [custom, setCustom] = useState(!!initial?.variety && !known);
  const [form, setForm] = useState({
    variety: initial?.variety || "",
    date_planted: initial?.date_planted || "",
    maturity_status: initial?.maturity_status || "",
    canopy_height_ft: initial?.canopy_height_ft ?? "",
    health_assessment: initial?.health_assessment || "",
    soil_moisture_rating: initial?.soil_moisture_rating || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (locked) {
    return (
      <div data-testid="fruit-profile-locked" className="mt-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="flex items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700"><Lock className="h-6 w-6" /></div>
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-emerald-950">Fruit Tree Profile Tracking — Available on Orchardist and above</h3>
            <p className="mt-1 text-sm text-stone-500">Track variety, maturity, canopy height, health and soil moisture for every fruit tree in your grove.</p>
            <Button data-testid="fruit-profile-upgrade-btn" onClick={() => navigate("/pricing")} className="mt-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white">
              <Sparkles className="mr-1 h-4 w-4" /> Upgrade
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        variety: form.variety || null,
        date_planted: form.date_planted || null,
        maturity_status: form.maturity_status || null,
        canopy_height_ft: form.canopy_height_ft === "" ? null : Number(form.canopy_height_ft),
        health_assessment: form.health_assessment || null,
        soil_moisture_rating: form.soil_moisture_rating || null,
      };
      await api.put(`/trees/${treeId}/fruit-profile`, payload);
      toast.success("Fruit tree profile saved");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="fruit-profile-card" className="mt-6 rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <h3 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Apple className="h-5 w-5 text-emerald-700" /> Fruit Tree Profile</h3>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {/* Variety */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-400">Tree Variety</Label>
          <Select
            value={custom ? "__custom__" : form.variety}
            onValueChange={(v) => {
              if (v === "__custom__") { setCustom(true); set("variety", ""); }
              else { setCustom(false); set("variety", v); }
            }}
          >
            <SelectTrigger data-testid="fp-variety-trigger" className="rounded-xl"><SelectValue placeholder="Select variety" /></SelectTrigger>
            <SelectContent>
              {VARIETIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              <SelectItem value="__custom__">Custom…</SelectItem>
            </SelectContent>
          </Select>
          {custom && (
            <Input data-testid="fp-variety-custom" value={form.variety} onChange={(e) => set("variety", e.target.value)} placeholder="Enter variety" className="rounded-xl" />
          )}
        </div>

        {/* Date Planted */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-400">Date Planted</Label>
          <Input data-testid="fp-date-planted" type="date" value={form.date_planted} onChange={(e) => set("date_planted", e.target.value)} className="rounded-xl" />
        </div>

        {/* Maturity Status */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-400">Maturity Status</Label>
          <Select value={form.maturity_status} onValueChange={(v) => set("maturity_status", v)}>
            <SelectTrigger data-testid="fp-maturity-trigger" className="rounded-xl"><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>{MATURITY.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Canopy Height */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-400">Canopy Height (feet)</Label>
          <Input data-testid="fp-canopy-height" type="number" min="0" step="0.5" value={form.canopy_height_ft} onChange={(e) => set("canopy_height_ft", e.target.value)} placeholder="e.g. 8" className="rounded-xl" />
        </div>

        {/* Health Assessment */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-400">Health Assessment</Label>
          <Select value={form.health_assessment} onValueChange={(v) => set("health_assessment", v)}>
            <SelectTrigger data-testid="fp-health-trigger" className="rounded-xl"><SelectValue placeholder="Select assessment" /></SelectTrigger>
            <SelectContent>{HEALTH.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {/* Soil Moisture Rating */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-stone-400">Soil Moisture Rating</Label>
          <Select value={form.soil_moisture_rating} onValueChange={(v) => set("soil_moisture_rating", v)}>
            <SelectTrigger data-testid="fp-moisture-trigger" className="rounded-xl"><SelectValue placeholder="Select rating" /></SelectTrigger>
            <SelectContent>{MOISTURE.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button data-testid="fruit-profile-save-btn" onClick={save} disabled={busy} className="mt-6 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
        {busy ? "Saving…" : "Save profile"}
      </Button>
    </div>
  );
};
